import styled from 'styled-components';
import { textStyleCss } from 'src/theme/atlas';

export const SectionContainer = styled.section`
  display: flex;
  flex-direction: column;
  gap: 20px;
  margin-bottom: 40px;
`;

export const SectionHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 16px;
  row-gap: 12px;
`;

export const CustomTitleWithCounter = styled.div`
  display: flex;
  flex-direction: row;
  column-gap: 8px;
  align-items: center;
  min-width: 0;
`;

export const CountryFilterWrapper = styled.div`
  flex-shrink: 0;
  margin-left: auto;
  align-items: center;
  display: flex;
`;

export const TitleText = styled.p<{ $variant?: 'default' | 'headingH3Bold' }>`
  ${({ theme, $variant }) =>
    $variant === 'headingH3Bold'
      ? textStyleCss(theme.atlas.typography.semantic.headingH3)
      : theme.isVaultVariant
        ? theme.newTypography.textStyleBodyLargeSemibold
        : textStyleCss(theme.atlas.typography.semantic.body)};
  font-weight: ${({ $variant }) =>
    $variant === 'headingH3Bold' ? 700 : 'inherit'};
  color: ${(props) =>
    props.theme.isVaultVariant
      ? props.theme.newPalette.neutral.N800
      : props.theme.atlas.semantic.color.type.default};
  word-break: break-word;
  margin: 0;
`;

export const CardsContainerWrapper = styled.div<{
  $showLeftGradient: boolean;
  $showRightGradient: boolean;
  $isExpanded: boolean;
}>`
  position: relative;
  width: 100%;
  overflow: visible;

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: -1px;
    bottom: 8px;
    width: 121px;
    background: linear-gradient(
      to right,
      ${(props) =>
          props.theme.isVaultVariant
            ? props.theme.newPalette.neutral.N200
            : props.theme.atlas.semantic.color.surface.variant}
        0%,
      transparent 100%
    );
    pointer-events: none;
    z-index: 1;
    opacity: ${(props) =>
      props.$showLeftGradient && !props.$isExpanded ? 1 : 0};
    transition: opacity 0.2s ease;
  }

  &::after {
    content: '';
    position: absolute;
    top: 0;
    right: -1px;
    bottom: 8px;
    width: 121px;
    background: linear-gradient(
      to right,
      transparent 0%,
      ${(props) =>
          props.theme.isVaultVariant
            ? props.theme.newPalette.neutral.N200
            : props.theme.atlas.semantic.color.surface.variant}
        100%
    );
    pointer-events: none;
    z-index: 1;
    opacity: ${(props) =>
      props.$showRightGradient && !props.$isExpanded ? 1 : 0};
    transition: opacity 0.2s ease;
  }
`;

export const CardsContainer = styled.div<{ $isExpanded: boolean }>`
  display: ${(props) => (props.$isExpanded ? 'grid' : 'flex')};
  grid-template-columns: ${(props) =>
    props.$isExpanded ? 'repeat(3, 1fr)' : 'none'};
  gap: 20px;
  overflow-x: ${(props) => (props.$isExpanded ? 'hidden' : 'auto')};
  overflow-y: ${(props) => (props.$isExpanded ? 'auto' : 'visible')};
  padding-bottom: 8px;
  align-items: flex-start;
  min-height: 0;
  width: 100%;
  margin: 0;
  padding-right: 0;
  justify-content: ${(props) => (props.$isExpanded ? 'start' : 'flex-start')};
  ${(props) =>
    props.$isExpanded
      ? `
    max-height: 600px;
  `
      : ''}

  ${(props) => (!props.theme.isVaultVariant ? 'scrollbar-width: none;' : '')}

  &::-webkit-scrollbar {
    ${(props) => (props.$isExpanded ? 'width: 8px;' : 'height: 8px;')}
    ${(props) => (!props.theme.isVaultVariant ? 'display: none;' : '')}
  }

  &::-webkit-scrollbar-track {
    background: ${(props) =>
      props.theme.isVaultVariant
        ? props.theme.newPalette.neutral.N200
        : props.theme.atlas.semantic.color.surface.variant};
    border-radius: 4px;
  }

  &::-webkit-scrollbar-thumb {
    background: ${(props) =>
      props.theme.isVaultVariant
        ? props.theme.newPalette.neutral.N400
        : props.theme.atlas.semantic.color.ui.scrollbar.handle};
    border-radius: 4px;

    &:hover {
      background: ${(props) =>
        props.theme.isVaultVariant
          ? props.theme.newPalette.neutral.N500
          : props.theme.atlas.core.color.gray[500]};
    }
  }

  &:hover,
  &:focus-within {
    scrollbar-width: thin;

    &::-webkit-scrollbar {
      display: block;
    }
  }
`;

export const CardWrapper = styled.div<{ $isExpanded: boolean }>`
  flex: 0 0 auto;
  width: ${(props) => (props.$isExpanded ? '100%' : '349px')};
  min-width: ${(props) => (props.$isExpanded ? '0' : '349px')};
  max-width: ${(props) => (props.$isExpanded ? '100%' : '349px')};
  display: flex;
  align-items: flex-start;
  overflow: visible;
`;

export const EmptyStateContainer = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  gap: 16px;
  width: 100%;
  padding: 40px 0;
`;

export const LoadingContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  width: 100%;
  min-height: 400px;
  padding: 40px 0;
`;

export const CategoryConfigurationButton = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  box-sizing: border-box;
  padding: 8px 16px;
  border-radius: ${(props) => props.theme.atlas.semantic.radius.sm};
  border: none;
  cursor: pointer;
  background-color: ${(props) =>
    props.theme.isVaultVariant
      ? props.theme.newPalette.turquoise.T300
      : props.theme.atlas.semantic.color.action.primary.default};
  color: ${(props) =>
    props.theme.isVaultVariant
      ? props.theme.newPalette.neutral.N0
      : props.theme.atlas.semantic.color.action.primary.onPrimary};
  ${({ theme }) => textStyleCss(theme.atlas.typography.semantic.bodySmall)};

  &:hover {
    filter: brightness(0.95);
  }

  &:focus-visible {
    outline: 2px solid
      ${(props) =>
        props.theme.isVaultVariant
          ? props.theme.newPalette.turquoise.T400
          : props.theme.atlas.semantic.color.action.primary.hover};
    outline-offset: 2px;
  }
`;

export const ShowAllLink = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  margin-top: 8px;
  margin-left: auto;
  margin-right: auto;
  background: none;
  border: none;
  padding: 4px 12px;
  cursor: pointer;
  ${({ theme }) =>
    theme.isVaultVariant
      ? theme.newTypography.textStyleBodySemibold
      : textStyleCss(theme.atlas.typography.semantic.bodySmall)}
  color: ${(props) =>
    props.theme.isVaultVariant
      ? props.theme.newPalette.neutral.N800
      : props.theme.atlas.semantic.color.type.default};
  text-decoration: none;
  width: fit-content;
  font-weight: 600;
  border-radius: 8px;

  svg,
  i {
    cursor: pointer;
  }

  &:hover {
    color: ${(props) =>
      props.theme.isVaultVariant
        ? props.theme.newPalette.neutral.N900
        : props.theme.atlas.core.color.gray[950]};
    cursor: pointer;

    svg,
    i {
      color: ${(props) =>
        props.theme.isVaultVariant
          ? props.theme.newPalette.neutral.N900
          : props.theme.atlas.core.color.gray[950]};
      cursor: pointer;
    }
  }

  &:active {
    color: ${(props) =>
      props.theme.isVaultVariant
        ? props.theme.newPalette.neutral.N800
        : props.theme.atlas.semantic.color.type.default};
    cursor: pointer;
  }

  &:focus {
    outline: none;
  }

  &:focus-visible {
    outline: 2px solid
      ${(props) =>
        props.theme.isVaultVariant
          ? props.theme.newPalette.turquoise.T300
          : props.theme.atlas.semantic.color.ui.focusRing};
    outline-offset: 2px;
    border-radius: 4px;
  }
`;
